import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { getSocket } from '../lib/socket';
import Badge         from '../components/ui/Badge';
import Icon          from '../components/ui/Icon';
import PullToRefresh from '../components/ui/PullToRefresh';
import { useAuth }   from '../context/AuthContext';
import { getTicketsApi } from '../api/ticket';
import {
  useTicket,
  useCreateTicket,
  useReplyToTicket,
  useCloseTicket,
} from '../hooks/useTicket';

/* ── API response normalizers ─────────────────────────────────────────────── */
const normalizeTicket = (t) => ({
  id:          t.ticket_id,
  subject:     t.subject     ?? '(no subject)',
  description: t.description ?? '',
  status:      t.status      ?? 'open',
  createdBy:   t.created_by  ?? null,
  hostId:      t.host_id     ?? null,
  createdAt:   t.created_at  ?? null,
  updatedAt:   t.updated_at  ?? null,
});

const normalizeReply = (r) => ({
  id:        r.reply_id,
  ticketId:  r.ticket_id,
  body:      r.message       ?? '',
  sender:    r.sender_role   ?? 'host',
  sentBy:    r.sent_by       ?? null,
  createdAt: r.created_at    ?? null,
});

/* ── Constants ────────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  open:        { color: 'blue',   label: 'Open'        },
  in_progress: { color: 'orange', label: 'In Progress' },
  resolved:    { color: 'green',  label: 'Resolved'    },
  closed:      { color: 'gray',   label: 'Closed'      },
};

const FILTERS = [
  { key: 'all',         label: 'All'        },
  { key: 'open',        label: 'Open'       },
  { key: 'in_progress', label: 'Active'     },
  { key: 'resolved',    label: 'Resolved'   },
  { key: 'closed',      label: 'Closed'     },
];

const is401or403 = (e) => {
  const msg = (e?.message ?? '').toLowerCase();
  return msg.includes('401') || msg.includes('403') ||
         msg.includes('unauthorized') || msg.includes('forbidden');
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d      = new Date(iso);
  const diffMs = Date.now() - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/* ── Empty State ──────────────────────────────────────────────────────────── */
const EmptyState = ({ onNew }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center px-8 animate-fadeUp"
       style={{ animationFillMode: 'forwards' }}>
    <div className="w-16 h-16 rounded-2xl bg-surface-700 border border-surface-500 flex items-center justify-center mb-4">
      <Icon name="msgCircle" size={26} className="text-surface-300" />
    </div>
    <p className="font-syne font-bold text-slate-800 text-base mb-1">No tickets yet</p>
    <p className="font-dm text-sm text-surface-200 mb-5">
      Reach out to our admin team for any help or questions.
    </p>
    <button
      onClick={onNew}
      className="flex items-center gap-2 px-5 py-2.5 rounded-full font-dm font-semibold text-sm text-white press-scale"
      style={{ background: 'linear-gradient(135deg, #4338ca, #6366f1)' }}
    >
      <Icon name="plus" size={15} className="text-white" strokeWidth="2.5" />
      Raise a Ticket
    </button>
  </div>
);

/* ── Loading Skeleton ─────────────────────────────────────────────────────── */
const ListSkeleton = () => (
  <div className="px-5 flex flex-col gap-4 pt-2">
    {[1, 2, 3].map((n) => (
      <div key={n} className="bg-surface-800 rounded-2xl p-4 border border-surface-500 card-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl shimmer-bg flex-shrink-0" />
            <div>
              <div className="h-3.5 w-40 shimmer-bg rounded mb-2" />
              <div className="h-2.5 w-24 shimmer-bg rounded" />
            </div>
          </div>
          <div className="w-16 h-5 shimmer-bg rounded-full" />
        </div>
        <div className="h-3 w-56 shimmer-bg rounded mt-1" />
      </div>
    ))}
  </div>
);

/* ── Error State ──────────────────────────────────────────────────────────── */
const ErrorState = ({ message, onRetry }) => (
  <div className="px-5 flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mb-3">
      <Icon name="help" size={22} className="text-rose-400" />
    </div>
    <p className="font-dm text-sm text-surface-200 mb-3">{message || 'Something went wrong.'}</p>
    {onRetry && (
      <button onClick={onRetry} className="font-dm text-xs text-brand-600 font-semibold">
        Try again
      </button>
    )}
  </div>
);

/* ── Ticket List Card ─────────────────────────────────────────────────────── */
const TicketCard = ({ ticket, delay, onSelect }) => {
  const cfg = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.open;

  return (
    <button
      onClick={() => onSelect(ticket.id)}
      className="w-full text-left bg-surface-800 rounded-2xl p-4 card-shadow animate-fadeUp border border-surface-500 press-scale"
      style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}
          >
            <Icon name="ticket" size={18} className="text-slate-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-syne font-bold text-slate-800 text-sm leading-snug truncate">
              {ticket.subject}
            </p>
            <p className="font-dm text-surface-300 text-xs mt-0.5">{ticket.id}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <Badge label={cfg.label} color={cfg.color} />
          <span className="font-dm text-[10px] text-surface-300">{fmtDate(ticket.updatedAt)}</span>
        </div>
      </div>

      {ticket.description && (
        <div className="pl-[52px] mt-1">
          <p className="font-dm text-xs text-surface-200 line-clamp-2 leading-relaxed">
            {ticket.description}
          </p>
        </div>
      )}

      <div className="flex items-center justify-end gap-1 mt-3 pl-[52px]">
        <span className="font-dm text-xs text-brand-600 font-medium">View thread</span>
        <Icon name="arrow" size={11} className="text-brand-600" />
      </div>
    </button>
  );
};

/* ── Message Bubble ───────────────────────────────────────────────────────── */
const MessageBubble = ({ reply }) => {
  const isHost = reply.sender === 'host';
  return (
    <div className={`flex ${isHost ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isHost && (
        <div className="w-7 h-7 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center mr-2 flex-shrink-0 self-end mb-1">
          <Icon name="shieldCheck" size={13} className="text-brand-600" />
        </div>
      )}
      <div className={`max-w-[80%] flex flex-col ${isHost ? 'items-end' : 'items-start'}`}>
        {!isHost && (
          <span className="font-dm text-[10px] text-surface-300 mb-1 px-1">Admin</span>
        )}
        <div
          className={`px-3.5 py-2.5 rounded-2xl font-dm text-sm leading-relaxed ${
            isHost
              ? 'rounded-br-sm text-white'
              : 'rounded-bl-sm bg-surface-700 border border-surface-500 text-slate-800'
          }`}
          style={isHost ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)' } : {}}
        >
          {reply.body}
        </div>
        <span className={`font-dm text-[10px] text-surface-300 mt-1 px-1 ${isHost ? 'text-right' : 'text-left'}`}>
          {fmtDate(reply.createdAt)}
        </span>
      </div>
    </div>
  );
};

/* ── Ticket Detail View ───────────────────────────────────────────────────── */
const TicketDetail = ({ ticketId, onBack }) => {
  const { data, isLoading, isError, error, refetch } = useTicket(ticketId);
  const { mutate: reply,  isPending: isSending  } = useReplyToTicket();
  const { mutate: close,  isPending: isClosing  } = useCloseTicket();
  const { logout } = useAuth();
  const [text,         setText]         = useState('');
  const [liveReplies,  setLiveReplies]  = useState([]);
  const [liveStatus,   setLiveStatus]   = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (error && (error.message?.includes('401') || error.message?.toLowerCase().includes('unauthorized'))) {
      logout();
    }
  }, [error, logout]);

  // ── Socket: join room, listen for live updates, leave on unmount ──────────
  useEffect(() => {
    if (!ticketId) return;

    const socket = getSocket();

    const joinRoom = () => socket.emit('join_ticket', ticketId);

    if (socket.connected) {
      joinRoom();
    }
    socket.on('connect', joinRoom);

    const onNewReply = (raw) => {
      if (raw.ticket_id !== ticketId) return;
      setLiveReplies((prev) => {
        const normalized = normalizeReply(raw);
        if (prev.some((r) => r.id === normalized.id)) return prev;
        return [...prev, normalized];
      });
    };

    const onStatusChanged = ({ ticket_id, status }) => {
      if (ticket_id !== ticketId) return;
      setLiveStatus(status);
    };

    socket.on('new_reply', onNewReply);
    socket.on('ticket_status_changed', onStatusChanged);

    return () => {
      socket.emit('leave_ticket', ticketId);
      socket.off('connect', joinRoom);
      socket.off('new_reply', onNewReply);
      socket.off('ticket_status_changed', onStatusChanged);
    };
  }, [ticketId]);

  const restReplies = data?.replies ? data.replies.map(normalizeReply) : [];
  const ticket      = data?.ticket  ? normalizeTicket(data.ticket)     : null;

  const replies = useMemo(() => {
    const seen = new Set();
    return [...restReplies, ...liveReplies].filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, liveReplies]);

  const effectiveStatus = liveStatus ?? ticket?.status ?? 'open';
  const cfg      = STATUS_CONFIG[effectiveStatus] ?? STATUS_CONFIG.open;
  const isClosed = effectiveStatus === 'closed' || effectiveStatus === 'resolved';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies.length]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;
    reply({ ticketId, message: trimmed }, { onSuccess: () => setText('') });
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-surface-500 bg-surface-800 flex-shrink-0">
        <button onClick={onBack}
          className="w-8 h-8 rounded-xl bg-surface-700 flex items-center justify-center press-scale flex-shrink-0">
          <Icon name="arrowLeft" size={16} className="text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          {isLoading
            ? <div className="h-3.5 w-44 shimmer-bg rounded" />
            : <p className="font-syne font-bold text-slate-800 text-sm truncate">{ticket?.subject}</p>
          }
          {cfg && <div className="mt-0.5"><Badge label={cfg.label} color={cfg.color} /></div>}
        </div>
        {ticket && !isClosed && (
          <button
            onClick={() => close(ticketId)}
            disabled={isClosing}
            className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-surface-700 border border-surface-500 font-dm font-semibold text-xs text-surface-200 disabled:opacity-50 press-scale"
          >
            {isClosing ? 'Closing…' : 'Close'}
          </button>
        )}
      </div>

      {/* Ticket meta bar */}
      {ticket && (
        <div className="px-4 py-2 border-b border-surface-500 bg-surface-700 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Icon name="ticket" size={11} className="text-surface-300" />
              <span className="font-dm text-xs text-surface-200">{ticket.id}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="clock" size={11} className="text-surface-300" />
              <span className="font-dm text-xs text-surface-200">Opened {fmtDate(ticket.createdAt)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Ticket description — thread starter */}
      {ticket?.description && (() => {
        const isHost = ticket.createdBy && ticket.hostId && ticket.createdBy === ticket.hostId;
        const senderLabel = isHost ? 'Host' : 'Admin';
        return (
          <div className="px-4 pt-4 pb-0 flex-shrink-0">
            <div className={`flex gap-3 ${isHost ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-700 to-brand-500 flex items-center justify-center">
                  <Icon name={isHost ? 'user' : 'shieldCheck'} size={14} className="text-white" />
                </div>
                <div className="w-0.5 flex-1 bg-surface-500 mt-2 min-h-[24px]" />
              </div>
              <div className={`flex-1 pb-4 flex flex-col ${isHost ? 'items-end' : 'items-start'}`}>
                <div className={`flex items-center gap-2 mb-1 ${isHost ? 'flex-row-reverse' : 'flex-row'}`}>
                  <span className="font-dm text-xs font-bold text-slate-700">{senderLabel}</span>
                  <span className="font-dm text-[10px] text-surface-300">·</span>
                  <span className="font-dm text-[10px] text-surface-300 uppercase tracking-wide">Original Message</span>
                  <span className="font-dm text-[10px] text-surface-300">{fmtDate(ticket.createdAt)}</span>
                </div>
                <div className={`max-w-[85%] bg-gradient-to-br from-brand-700 to-brand-500 px-4 py-3 rounded-2xl ${isHost ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
                  <p className="font-dm text-sm text-white leading-relaxed">{ticket.description}</p>
                </div>
              </div>
            </div>
            <div className={`flex items-center gap-2 pb-3 ${isHost ? 'pr-11' : 'pl-11'}`}>
              <div className="flex-1 h-px bg-surface-500" />
              <span className="font-dm text-[11px] text-surface-300">Thread replies</span>
              <div className="flex-1 h-px bg-surface-500" />
            </div>
          </div>
        );
      })()}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4" style={{ overscrollBehavior: 'contain' }}>
        {isLoading && (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`flex ${n % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div className={`h-12 rounded-2xl shimmer-bg ${n % 2 === 0 ? 'w-48' : 'w-56'}`} />
              </div>
            ))}
          </div>
        )}

        {isError && !isLoading && (
          <ErrorState message={error?.message} onRetry={refetch} />
        )}

        {!isLoading && !isError && replies.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="msgCircle" size={28} className="text-surface-300 mb-2" />
            <p className="font-dm text-sm text-surface-200">No messages yet. Start the conversation!</p>
          </div>
        )}

        {!isLoading && !isError && replies.map((r) => (
          <MessageBubble key={r.id} reply={r} />
        ))}

        <div ref={bottomRef} />
      </div>

      {/* Reply input / closed notice */}
      {!isLoading && !isError && (
        isClosed ? (
          <div className="px-4 py-3 border-t border-surface-500 flex items-center justify-center gap-2 bg-surface-700 flex-shrink-0">
            <Icon name="check" size={13} className="text-emerald-500" />
            <p className="font-dm text-xs text-surface-200 capitalize">
              This ticket is {effectiveStatus}.
            </p>
          </div>
        ) : (
          <div className="flex items-end gap-2 px-4 py-3 border-t border-surface-500 bg-surface-800 flex-shrink-0 pb-5">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type your message…"
              rows={1}
              className="flex-1 resize-none bg-surface-700 border border-surface-500 rounded-2xl px-4 py-2.5 font-dm text-sm text-slate-800 placeholder-surface-300 focus:outline-none focus:border-brand-400 transition-colors"
              style={{ maxHeight: 96, overflowY: 'auto' }}
            />
            <button
              onClick={handleSend}
              disabled={!text.trim() || isSending}
              className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 press-scale disabled:opacity-40 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #4338ca, #6366f1)' }}
            >
              {isSending
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Icon name="send" size={16} className="text-white" />
              }
            </button>
          </div>
        )
      )}
    </div>
  );
};

/* ── New Ticket Form ──────────────────────────────────────────────────────── */
const NewTicketForm = ({ onBack, onSuccess }) => {
  const { mutate: create, isPending } = useCreateTicket();
  const [subject,     setSubject]     = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;
    create(
      { subject: subject.trim(), description: description.trim() },
      { onSuccess }
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-surface-500 bg-surface-800 flex-shrink-0">
        <button onClick={onBack}
          className="w-8 h-8 rounded-xl bg-surface-700 flex items-center justify-center press-scale">
          <Icon name="arrowLeft" size={16} className="text-slate-600" />
        </button>
        <p className="font-syne font-bold text-slate-800 text-base flex-1">New Ticket</p>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-5">

        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-brand-50 border border-brand-200">
          <Icon name="msgCircle" size={16} className="text-brand-600 flex-shrink-0 mt-0.5" />
          <p className="font-dm text-xs text-brand-700 leading-relaxed">
            Our admin team typically responds within 2–4 hours during working hours.
          </p>
        </div>

        <div>
          <label className="block font-dm font-semibold text-xs text-surface-200 uppercase tracking-wider mb-2">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of your issue"
            maxLength={120}
            className="w-full bg-surface-700 border border-surface-500 rounded-2xl px-4 py-3 font-dm text-sm text-slate-800 placeholder-surface-300 focus:outline-none focus:border-brand-400 transition-colors"
          />
        </div>

        <div>
          <label className="block font-dm font-semibold text-xs text-surface-200 uppercase tracking-wider mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your issue in detail…"
            rows={5}
            className="w-full resize-none bg-surface-700 border border-surface-500 rounded-2xl px-4 py-3 font-dm text-sm text-slate-800 placeholder-surface-300 focus:outline-none focus:border-brand-400 transition-colors"
          />
        </div>

        <button
          type="submit"
          disabled={!subject.trim() || !description.trim() || isPending}
          className="w-full py-3.5 rounded-2xl font-dm font-bold text-sm text-white press-scale disabled:opacity-50 transition-opacity"
          style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
        >
          {isPending
            ? <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting…
              </span>
            : 'Submit Ticket'
          }
        </button>
      </form>
    </div>
  );
};

/* ── Filter Chips ─────────────────────────────────────────────────────────── */
const FilterChips = ({ active, onChange }) => (
  <div className="flex gap-2 overflow-x-auto flex-1 pb-1" style={{ scrollbarWidth: 'none' }}>
    {FILTERS.map((f) => {
      const isActive = active === f.key;
      return (
        <button
          key={f.key}
          onClick={() => onChange(f.key)}
          className="flex-shrink-0 px-3.5 py-2 rounded-full font-dm font-semibold text-xs transition-all duration-200"
          style={
            isActive
              ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)', color: '#fff', border: '1px solid transparent' }
              : { background: 'var(--surface-800, #f8fafc)', color: 'var(--surface-200, #94a3b8)', border: '1px solid var(--surface-500, #e2e8f0)' }
          }
        >
          {f.label}
        </button>
      );
    })}
  </div>
);

/* ── Main Page ────────────────────────────────────────────────────────────── */
const TicketsPage = () => {
  const [view,         setView]         = useState('list');
  const [selectedId,   setSelectedId]   = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchInput,  setSearchInput]  = useState('');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [items,        setItems]        = useState([]);
  const [page,         setPage]         = useState(1);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [hasMore,      setHasMore]      = useState(true);
  const [fetchError,   setFetchError]   = useState(null);
  const { logout } = useAuth();

  // Debounce search input → searchQuery (400 ms)
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(async (pageNum, replace = false) => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = { page: pageNum, limit: 20 };
      if (activeFilter !== 'all') params.status = activeFilter;
      if (searchQuery)            params.search  = searchQuery;

      const res = await getTicketsApi(params);
      const { data, total: t, totalPages } = res;
      setItems(prev => replace ? data.map(normalizeTicket) : [...prev, ...data.map(normalizeTicket)]);
      setTotal(t);
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (e) {
      if (is401or403(e)) { logout(); return; }
      setFetchError(e);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchQuery, logout]);

  // Refetch from page 1 whenever filters or search change
  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  // Keep mutable refs so the IntersectionObserver closure is always current
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  const pageRef    = useRef(page);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { pageRef.current    = page;    }, [page]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
          fetchPage(pageRef.current + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchPage]); // recreated only when filters/search change

  // Socket: update ticket status in list view in real time
  useEffect(() => {
    if (view !== 'list') return;
    const socket = getSocket();
    const onStatusChanged = ({ ticket_id, status }) => {
      setItems(prev => prev.map(t => t.id === ticket_id ? { ...t, status } : t));
    };
    socket.on('ticket_status_changed', onStatusChanged);
    return () => socket.off('ticket_status_changed', onStatusChanged);
  }, [view]);

  /* ── Detail view ── */
  if (view === 'detail' && selectedId) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <TicketDetail
          ticketId={selectedId}
          onBack={() => { setView('list'); setSelectedId(null); fetchPage(1, true); }}
        />
      </div>
    );
  }

  /* ── New ticket view ── */
  if (view === 'new') {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <NewTicketForm
          onBack={() => setView('list')}
          onSuccess={() => { setView('list'); fetchPage(1, true); }}
        />
      </div>
    );
  }

  /* ── List view ── */
  return (
    <PullToRefresh onRefresh={() => fetchPage(1, true)}>
      <div className="pb-6">

        {/* Filters + New button */}
        <div className="pt-4 pb-3 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-2 px-5">
            <FilterChips active={activeFilter} onChange={setActiveFilter} />
            <button
              onClick={() => setView('new')}
              className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full font-dm font-semibold text-xs text-white press-scale"
              style={{ background: 'linear-gradient(135deg, #4338ca, #6366f1)' }}
            >
              <Icon name="plus" size={13} className="text-white" strokeWidth="2.5" />
              New
            </button>
          </div>
        </div>

        {/* Search input */}
        <div className="px-5 pb-3">
          <div className="relative">
            <Icon name="search" size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-300 pointer-events-none" />
            <input
              type="text"
              placeholder="Search tickets…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-surface-700 border border-surface-500 rounded-2xl font-dm text-sm text-slate-800 placeholder-surface-300 focus:outline-none focus:border-brand-400 transition-colors"
            />
          </div>
        </div>

        {/* Total count badge */}
        {total > 0 && (
          <div className="px-5 pb-2">
            <span className="font-dm text-xs text-surface-200">
              {total} ticket{total !== 1 ? 's' : ''}
              {activeFilter !== 'all' ? ` · ${FILTERS.find(f => f.key === activeFilter)?.label}` : ''}
            </span>
          </div>
        )}

        {/* Loading */}
        {loading && items.length === 0 && <ListSkeleton />}

        {/* Error */}
        {fetchError && !loading && (
          <ErrorState message={fetchError?.message} onRetry={() => fetchPage(1, true)} />
        )}

        {/* Empty */}
        {!loading && !fetchError && items.length === 0 && (
          <EmptyState onNew={() => setView('new')} />
        )}

        {/* Content */}
        {!fetchError && items.length > 0 && (
          <div className="px-5 flex flex-col gap-4">
            {items.map((t, i) => (
              <TicketCard
                key={t.id}
                ticket={t}
                delay={`${0.07 * i}s`}
                onSelect={(id) => { setSelectedId(id); setView('detail'); }}
              />
            ))}
          </div>
        )}

        {/* Scroll sentinel — triggers next page load when it enters the viewport */}
        <div ref={sentinelRef} className="h-1" />

        {/* Inline spinner while fetching additional pages */}
        {loading && items.length > 0 && (
          <div className="flex justify-center py-4">
            <span className="w-5 h-5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

      </div>
    </PullToRefresh>
  );
};

export default TicketsPage;
